/**
 * panelAnim — the group / ungroup micro-interactions (design option A: glide &
 * settle), plus the width morph when a panel and the 264px group/tabbed panel
 * differ in width (the format panel is content-width, ~150px).
 *
 * Driven imperatively on the panel's DOM node rather than via React state: a
 * one-shot transform/width/opacity tween layered on top of the styles React
 * owns (left/top, and the group's fixed width) — so the two never fight. Each
 * animated property is reset when the tween ends so React fully owns it again.
 *
 * Shapes:
 *  • groupIn  — the tabbed group appears: scale + fade in, and its width grows
 *               from the panel it formed over (targetWidth) to GROUP_WIDTH.
 *  • glideFrom— a panel returning from the group eases FROM the group's old spot
 *               to its home position (FLIP), and its width morphs from
 *               GROUP_WIDTH back to its natural width.
 *  • settle   — a torn-off panel, already dropped at its own width under the
 *               pointer, relaxes from slightly-lifted (scale) down to rest.
 *
 * Honors prefers-reduced-motion (no-op → things just appear in place).
 */

/** Width of the tabbed group and the Add palette (the format panel is narrower
 *  and content-sized). Panels coming from / going to the group morph to/from
 *  this. Keep in sync with the width in PanelDockGroup / AddSectionPalette. */
export const GROUP_WIDTH = 264;

const GLIDE_MS = 280;
const SETTLE_MS = 200;
const GROUP_MS = 220;
const EASE = "cubic-bezier(.22,.9,.24,1)"; // ease-out, no overshoot

function reducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

interface Range {
  readonly from: number | string;
  readonly to: number | string;
  /** Value to leave on the node after the tween (default: clear to ""). */
  readonly reset?: string;
}
interface Spec {
  readonly ms: number;
  readonly transform?: Range;
  readonly width?: Range; // numbers are px
  readonly opacity?: Range;
}

const px = (v: number | string) => (typeof v === "number" ? `${v}px` : v);

/** Commit the `from` values (reflow), then set the `to` values with a
 *  transition; reset each property once done. */
function animate(el: HTMLElement, spec: Spec): void {
  if (reducedMotion()) return;
  const props: string[] = [];
  el.style.transition = "none";
  if (spec.transform) {
    el.style.transform = String(spec.transform.from);
    props.push("transform");
  }
  if (spec.width) {
    el.style.width = px(spec.width.from);
    props.push("width");
  }
  if (spec.opacity) {
    el.style.opacity = String(spec.opacity.from);
    props.push("opacity");
  }
  void el.offsetWidth; // force reflow so the `from` values are the start
  el.style.transition = props
    .map((p) => `${p} ${spec.ms}ms ${EASE}`)
    .join(", ");
  if (spec.transform) el.style.transform = String(spec.transform.to);
  if (spec.width) el.style.width = px(spec.width.to);
  if (spec.opacity) el.style.opacity = String(spec.opacity.to);
  window.setTimeout(() => {
    el.style.transition = "";
    if (spec.transform) el.style.transform = spec.transform.reset ?? "";
    if (spec.width) el.style.width = spec.width.reset ?? "";
    if (spec.opacity) el.style.opacity = spec.opacity.reset ?? "";
  }, spec.ms + 40);
}

/** The tabbed group appears: settle in and grow from the panel it docked over. */
export function groupIn(el: HTMLElement, fromWidth: number): void {
  const resizes = Math.abs(fromWidth - GROUP_WIDTH) > 2;
  animate(el, {
    ms: GROUP_MS,
    transform: { from: "scale(.97)", to: "scale(1)" },
    opacity: { from: 0.8, to: 1 },
    ...(resizes
      ? {
          width: {
            from: fromWidth,
            to: GROUP_WIDTH,
            reset: `${GROUP_WIDTH}px`,
          },
        }
      : {}),
  });
}

/** A panel returning from the group glides from `origin` (viewport top-left of
 *  the group) to its already-rendered home position, morphing its width back
 *  from the group's width to its natural width. */
export function glideFrom(
  el: HTMLElement,
  origin: { x: number; y: number },
): void {
  if (reducedMotion()) return;
  const natural = el.offsetWidth;
  const r = el.getBoundingClientRect();
  const dx = origin.x - r.left;
  const dy = origin.y - r.top;
  const moved = Math.abs(dx) > 1 || Math.abs(dy) > 1;
  const resizes = Math.abs(natural - GROUP_WIDTH) > 2;
  if (!moved && !resizes) return;
  animate(el, {
    ms: GLIDE_MS,
    transform: moved
      ? {
          from: `translate(${dx}px, ${dy}px) scale(.98)`,
          to: "translate(0, 0) scale(1)",
        }
      : undefined,
    width: resizes ? { from: GROUP_WIDTH, to: natural } : undefined,
  });
}

/** A torn-off panel, dropped under the pointer at its own width, settles from
 *  lifted to rest. (No width morph — the drag ghost already matched its size.) */
export function settle(el: HTMLElement): void {
  animate(el, {
    ms: SETTLE_MS,
    transform: { from: "scale(1.04)", to: "scale(1)" },
  });
}
